import {
  NJFormItem,
  NJIconButton,
  NJTag,
} from "@engie-group/fluid-design-system-react";
import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Session } from "@/types";
import { ContextUsageBar } from "./ContextUsageBar";
import {
  useCloseSession,
  useDeleteSession,
  useReopenSession,
  useUpdateSession,
} from "./useSessions";

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: "Active",
  CLOSED: "Closed",
};

/** A single session row (rename/pin/close/reopen/delete + expandable chat). */
export function SessionRow({ session }: { session: Session }) {
  const navigate = useNavigate();
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(session.title);
  const updateSession = useUpdateSession();
  const closeSession = useCloseSession();
  const reopenSession = useReopenSession();
  const deleteSession = useDeleteSession();

  const submitRename = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = title.trim();
    if (!trimmed || trimmed === session.title) {
      setRenaming(false);
      return;
    }
    updateSession.mutate(
      { sessionId: session.id, payload: { title: trimmed } },
      { onSuccess: () => setRenaming(false) },
    );
  };

  return (
    <div className="flex flex-col gap-2 py-3">
      <div className="flex flex-wrap items-center gap-2">
        {renaming ? (
          <form onSubmit={submitRename} className="flex-1">
            <NJFormItem
              id={`session-title-${session.id}`}
              name="session-title"
              label="Rename session"
              labelKind="floating"
              value={title}
              onChange={(event) =>
                setTitle((event.target as HTMLInputElement).value)
              }
              onBlur={submitRename}
            />
          </form>
        ) : (
          <span className="flex-1 truncate font-nj-bold text-nj-neutral-primary-default">
            {session.title}
          </span>
        )}
        <NJTag label={STATUS_LABELS[session.status] ?? session.status} />
        <NJIconButton
          icon="chat"
          label="Open conversation"
          onClick={() => navigate(`/chat/${session.id}`)}
        />
        <NJIconButton
          icon="push_pin"
          label={session.isPinned ? "Unpin" : "Pin"}
          ariaPressed={session.isPinned}
          onClick={() =>
            updateSession.mutate({
              sessionId: session.id,
              payload: { isPinned: !session.isPinned },
            })
          }
        />
        <NJIconButton
          icon="edit"
          label="Rename"
          onClick={() => setRenaming(true)}
        />
        {session.status === "ACTIVE" ? (
          <NJIconButton
            icon="lock"
            label="Close session"
            onClick={() => closeSession.mutate(session.id)}
          />
        ) : (
          <NJIconButton
            icon="lock_open"
            label="Reopen session"
            onClick={() => reopenSession.mutate(session.id)}
          />
        )}
        <NJIconButton
          icon="delete"
          label="Delete session"
          onClick={() => deleteSession.mutate(session.id)}
        />
      </div>
      <ContextUsageBar usage={session.contextUsage} />
    </div>
  );
}
